import { OptimizationVisit } from 'bindings/optimization/OptimizationVisit';
import arrow_down from 'images/icon/arrow_down.png';
import ClockImage from 'images/vendors/alarm-clock.svg';
import * as moment from 'moment';
import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import { toast } from 'react-toastify';
import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { VisitsOptimizationBody } from '../../../../typings/bindings/optimization/VisitsOptimizationBody';
import { getFormTemplates } from '../../../atoms/forms';
import { AEventTypes } from '../../../atoms/global/events';
import { AUsers } from '../../../atoms/global/users';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownOwners from '../../../components_v2/dropdown/DropdownOwners';
import Input from '../../../components_v2/input/Input';
import { InputMode } from '../../../components_v2/input/model/Model';
import PageLoader from '../../../components_v2/pageLoader/PageLoader';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { DayPicker, DayPickerModal } from '../../../components_v2/toolbarFilter/time/DatePicker';
import { dateToNaiveDateTime, momentToNaiveDateTime, useWindowDimensions } from '../../../components_v2/utils';
import storeLang from '../../../helpers/storeLang';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import {
	BorderColor,
	BorderColor2,
	DarkGreySidely,
	RedSidely,
	SidelyBlack,
	SurfaceColor
} from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { optimizeVisits, RetVisit, WEEK_DAY_LIST } from '../../calendar/EventsApi';
import { creationPopupDropdownStyle, Spacer } from '../../../components_v2/popup/style/CreationFormStyle';
import { FormTemplate } from '../../forms/jsonValidator';
import { Owner } from '../../orders/model/Model';
import Restricted from '../../permissions/Restricted';
import { FlexDiv } from '../../products/style';
import { InitialMapRow } from '../model';
import ModalCalendar, { HotelNightConstraint } from './ModalCalendar';
import { INPUT_HEIGHT } from '../../../styles/global/css/GlobalInput';
import { Panel } from './ModalRight';
import { Interconnection } from './model';

const MAX_COMPANIES = 100;

const Button = styled(DefaultButton)`
	margin: 0;
	width: 40%;
`;

const Titles = styled.div`
  font-family: Inter;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 21px;
  margin-bottom: 6px;
  color: color-mix(in oklab, ${DarkGreySidely} 80%, transparent);
`;

const Mandatory = styled.div`
  font-family: Inter;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 21px;
  color: ${RedSidely};
  display: inline;
`;

const ErrorMessage = styled.div`
  width: 100%;
  font-size: 11px;
  color: ${RedSidely};
`;


const DurationPickerStyle = styled.div`
  color: ${DarkGreySidely};
  text-decoration: underline;
  text-decoration-color: ${DarkGreySidely};
  font-size: x-small;
  font-family: Inter;
  min-width: 28px;
`;

const DurationPickerModalWrapper = styled.div<{ isOpen: boolean, top?: boolean }>`
	position: absolute;
	top: ${p => p.top ? -10 : 55}px;
	left: 46px;
	max-height: ${p => p.isOpen ? '270px' : '0px'};
	box-shadow: 0 2px 6px color-mix(in oklab, ${SidelyBlack} 5%, transparent),0 0 0 1px color-mix(in oklab, ${SidelyBlack} 7.0%, transparent);
	z-index: 10;
	${p => p.isOpen ? `
		&:after {
			content: " ";
			position: absolute;
			left:50%;
			${p.top ? 'top' : 'bottom'}: 100%;  /* At the top of the tooltip */
			margin-left: -5px;
			border-width: 5px;
			border-style: solid;
			border-color: ${p.top ? BorderColor2 : 'transparent'} transparent ${p.top ? 'transparent' : BorderColor2} transparent;
		}
	` : ''}
	translate: -50% ${p => p.top ? '-100%' : ''};
`;

export function SimpleTimePicker(props: { disabled?: boolean, currDate?: Date | null, onChange: (d: Date) => void, error?: string | null, placeholder?: string }) {
	const [isOpen, setIsOpen] = React.useState<{ isOpen: boolean, top?: boolean }>({ isOpen: false });
	const [search, setSearch] = React.useState<string>();
	const { height } = useWindowDimensions();

	return (
		<div style={{ width: '100%', position: 'relative' }}>
			<DurationPickerModalWrapper isOpen={isOpen.isOpen} top={isOpen.top}>
				{isOpen?.isOpen && <DayPickerModal
					search={search}
					noAbsolute
					future
					onClickOut={() => {
						setIsOpen({ isOpen: false });
						setSearch(undefined);
					}}
					noDayButton
					time={true}
					date={props.currDate ? moment(props.currDate) : undefined}
					noDate
					onDateChange={date => {
						setIsOpen({ isOpen: false });
						props.onChange(date.toDate());
						setSearch(undefined);
					}}
				/>}
			</DurationPickerModalWrapper>
			<Input
				onChange={setSearch}
				inputStyle={{
					containerWidth: '100%',
					width: '100%',
					borderRadius: '4px',
					padding: '14px',
				}}
				name={''}
				type='string'
				value={
					search !== undefined
						? search : props.currDate
							? moment(props.currDate).format('HH:mm') : undefined
				}
				mode={props.error ? InputMode.Error : undefined}
				onClick={e => {
					if (!props.disabled) setIsOpen({ isOpen: true, top: e.screenY > height / 2 + 80 });
				}}
				placeholder={props.placeholder ?? ''}
			/>
		</div>
	);
}

export type Duration = { op: 'min' | 'h', nb: string }

export function durationToSeconds(duration: Duration | undefined): number {
	if (!duration) return 0;
	return parseFloat(duration.nb) * 60 * (duration.op === 'min' ? 1 : 60);
}

function durationToMiliSeconds(duration: Duration | undefined): number {
	return durationToSeconds(duration) * 1000;
}

export function SimpleDurationPicker(props: {
	duration: Duration,
	setTimeOperator: (timeOrNumber: Partial<Duration>) => void,
	placeholder?: string,
	error?: string | null
	fullTranslation?: boolean
}) {
	const [dropdownOpen, setDropdownOpen] = React.useState(false);
	const translate = getTranslate(storeLang.getState().localize);

	function toggleDropdown() {
		setDropdownOpen(!dropdownOpen);
	}

	return (
		(<div style={{
			background: SurfaceColor,
			border: `1px solid ${props.error ? RedSidely : BorderColor}`,
			boxSizing: 'border-box',
			borderRadius: '4px',
			height: `${INPUT_HEIGHT}px`,
			width: '100%',
			display: 'flex',
			alignItems: 'center'
		}}>
			<Input
				type='text'
				value={props.duration.nb}
				onChange={nb => props.setTimeOperator({ nb: (nb as string).replace(/[^0-9.,]/g, '') })}
				placeholder={props.placeholder ? props.fullTranslation ? props.placeholder : (translate('map.modal_right.' + props.placeholder) as string) : undefined}
				inputStyle={{ border: 'none', containerWidth: 'calc(100% - 114px)', fontSize: '12px', color: props.error ? RedSidely : '', width: '100%' }}
				name={''}
			/>
			<ButtonDropdown
				isOpen={dropdownOpen}
				toggle={toggleDropdown}
				style={{ border: 'none' }}
			>
				<DropdownToggle id="dropdown-custom-components"
					style={{ background: SurfaceColor, border: 'none', padding: '0px 30px 0 10px' }} >
					<DurationPickerStyle>
						{props.duration.op}
						<img src={arrow_down} style={{ height: '5px' }} />
					</DurationPickerStyle>
				</DropdownToggle>
				<DropdownMenu>
					<DropdownItem eventKey="1" style={{ fontSize: 'x-small', fontFamily: 'Inter' }}
						onClick={() => {
							props.setTimeOperator({ op: 'min' });
						}}>
						min
					</DropdownItem>
					<DropdownItem eventKey="2" style={{ fontSize: 'x-small', fontFamily: 'Inter' }}
						onClick={() => {
							props.setTimeOperator({ op: 'h' });
						}}>
						h
					</DropdownItem>
				</DropdownMenu>
			</ButtonDropdown>
			<img src={ClockImage} style={{ width: '20px' }} />
		</div>)
	);
}

export function CheckBoxWorkdayList(props: { selectedDays: number[], setSelectedDays: (v: number[]) => void }) {
	const { selectedDays, setSelectedDays } = props;
	const options = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
		.map((e, i) => { return { label: translateToString('map.modal_right.' + e) as string, value: i }; });
	const name = 'createEventFromLasso';

	function handleChecked(isChecked: boolean, value: number) {
		const newValue = selectedDays;
		if (isChecked) {
			newValue.push(value);
		} else {
			newValue.splice(newValue.indexOf(value), 1);
		}
		setSelectedDays([...newValue]);
		localStorage.setItem('planEventWorkingDays', selectedDays.join(','));
	}

	return (
		<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(75px, 1fr))' }}>
			{options.map((option, i) => (
				<React.Fragment key={`check_box_day_of_weeks[${i}]`}>
					<label style={{ height: '30px', margin: '0' }} className='check-sidely'>
						<input
							name={`${name}[${i}]`}
							id={`${name}[${i}]`}
							type="checkbox"
							value={option.value}
							checked={selectedDays && selectedDays.includes(option.value)}
							onChange={e =>
								handleChecked(e.target.checked, option.value)
							}
						/>
						<div className="checkbox-style" style={{ top: '7px' }} />
						<span style={{
							display: 'inline',
							position: 'relative',
							fontFamily: 'Inter',
							fontStyle: 'normal',
							fontWeight: 'normal',
							fontSize: '12px',
							lineHeight: '21px',
							left: '20px'
						}}>{option.label}</span>
					</label>
				</React.Fragment>

			))}
		</div>
	);
}

function generateAjustMinBy15() {
	const date = new Date();
	const min = date.getMinutes();
	const moduloBy15 = min % 15;
	if (moduloBy15 > 0)
		date.setMinutes(min + (15 - moduloBy15));
	return date;
}

export default function CreateEventFromLasso(props: {
	type?: { id: number, name: string }
	panel?: Panel
	companies: InitialMapRow[]
	onBack: () => void
	onCreate?: () => void
	noLimit?: boolean
	onChildChange?: (b: boolean) => void
	v2?: boolean
	noTitle?: boolean
}) {
	const translate = getTranslate(storeLang.getState().localize);
	const translateString = (str: string) => (translate(str) as string);
	const [maxEvent, setMaxEvent] = React.useState(localStorage.getItem('planEventMaxEvent') ?? '');
	const [firstEventDate, setFirstEventDate] = React.useState<Date>(() => {
		const stored = localStorage.getItem('planEventFirstEventDate');
		return stored ? new Date(JSON.parse(stored)) : new Date();
	});
	const [firstEventHour, setFirstEventHour] = React.useState(() => {
		const stored = localStorage.getItem('planEventFirstEventHour');
		return stored ? new Date(JSON.parse(stored)) : generateAjustMinBy15();
	});
	const [hourStartDay, setHourStartDay] = React.useState(new Date(2000, 1, 1, Number(localStorage.getItem('planEventHourStartDay') ?? Number(localStorage.getItem('startCalendarHour') ?? 7)), 0));
	const [hourEndDay, setHourEndDay] = React.useState(new Date(2000, 1, 1, Number(localStorage.getItem('planEventHourEndDay') ?? 17), 0));
	const [pauseHour, setPauseHour] = React.useState<Date | null>(() => {
		const stored = localStorage.getItem('planEventPauseHour');
		return stored ? new Date(JSON.parse(stored)) : null;
	});
	const [durationPicker, setDurationPicker] = React.useState(() => {
		const stored = localStorage.getItem('planEventDurationPicker');
		return stored ? JSON.parse(stored) : { fst: { nb: '', op: 'min' }, snd: { nb: '', op: 'min' }, trd: { nb: '', op: 'min' } };
	});
	const [daysSelected, setDaysSelected] = React.useState(() => {
		const stored = localStorage.getItem('planEventWorkingDays');
		return stored ? stored.split(',').map(num => parseInt(num, 10)) : [0, 1, 2, 3, 4];
	}
	);
	const [globalError, setGlobalError] = React.useState<boolean>(false);
	const [buttonError, setButtonError] = React.useState<boolean>(false);
	const [modalOpen, setModalOpen_] = React.useState<boolean>(false);
	const [events, setEvents] = React.useState<any[]>([]);
	const [hotelNights, setHotelNights] = React.useState<HotelNightConstraint>();

	const owners = useRecoilValue(AUsers);
	const [owner, setOwner] = React.useState<Owner | undefined>(owners.find(o => o.isYou));

	const name: string = (props.type != null) ? translateString(`event.${props.type.name}`) : '';
	const eventTypes = useRecoilValue(AEventTypes);
	const [form, setForm] = React.useState<FormTemplate>(JSON.parse(localStorage.getItem('planEventForm') || '{}'));
	const [formTemplates, setFormTemplates] = React.useState<FormTemplate[]>([]);

	const [title, setTitle] = React.useState<string>();
	const [description, setDescription] = React.useState<string>();

	const setModalOpen = (b: boolean) => {
		setModalOpen_(b);
		props.onChildChange?.(b);
	};

	React.useEffect(() => {
		if (props.panel === Panel.MassEvents) {
			if (props.companies.length > MAX_COMPANIES) {
				toast.warn(translate('map.modal_right.warning_over_100_events') as string);
			}
		}
	}, [props.panel]);

	React.useEffect(() => {

		getFormTemplates().then(response => {
			setFormTemplates(response.instanceForms);
		});
	}, []);

	function getError(key: string, error?: boolean) {
		const localError = typeof (error) !== 'undefined' ? error : globalError;
		const order = {
			duration_each_event: 'fst',
			time_between_each_event: 'snd',
			duration_pause: 'trd'
		};
		switch (key) {
			case 'max_events_per_day':
				if (!(/^\d+$/.test(maxEvent) || maxEvent.length == 0)) {
					return translateString('map.modal_right.fill_this_with_numbers_only');
				} else if (maxEvent.length > 0 && parseFloat(maxEvent) <= 0) {
					return translateString('map.modal_right.must_be_greater_than_0');
				}
				return null;
			case 'first_event_hour':
				return (!firstEventHour && localError ? translateString('map.modal_right.fill_the_field') : null);
			case 'hour_end_day':
				return (!hourEndDay && localError ? translateString('map.modal_right.fill_the_field') : null);
			case 'hour_start_day':
				return (!hourStartDay && localError ? translateString('map.modal_right.fill_the_field') : null);
			case 'duration_each_event':
			case 'time_between_each_event':
			case 'duration_pause': {
				const orderKey = order[key];
				if (!(/^\d+$/.test(durationPicker[orderKey].nb) || durationPicker[orderKey].nb.length == 0)) {
					return translateString('map.modal_right.fill_this_with_numbers_only');
				} else if (durationPicker[orderKey].op === 'min' && durationPicker[orderKey].nb.length > 0) {
					return (parseFloat(durationPicker[orderKey].nb) > 60 * 60 * 24 ? translateString('map.modal_right.duration_bigger_than_a_day') : null);
				} else if (durationPicker[orderKey].op === 'h' && durationPicker[orderKey].nb.length > 0) {
					return (parseFloat(durationPicker[orderKey].nb) > 24 ? translateString('map.modal_right.duration_bigger_than_a_day') : null);
				} else if (durationPicker[orderKey].nb.length == 0 && orderKey == 'fst' && localError) {
					return translateString('map.modal_right.fill_the_field');
				} else if (durationPicker[orderKey].nb.length == 0 && orderKey == 'trd' && localError && (pauseHour != null)) {
					return translateString('map.modal_right.duration_is_needed_if_pause_time_is_setted');
				}
				return null;
			}
			case 'days_of_activity':
				if (localError && daysSelected.length == 0) {
					return translateString('map.modal_right.fill_the_field');
				}
				return null;
			default:
				return null;
		}
	}

	function asAnError() {
		const allKeys = [
			'max_events_per_day',
			'first_event_hour',
			'duration_each_event',
			'time_between_each_event',
			'duration_pause',
			'days_of_activity',
			'hour_end_day',
			'hour_start_day',
			'departure_address'
		];
		return (allKeys.map(e => getError(e, true)).some(e => e !== null));
	}

	async function createEventsList(companies?: any[], interconnections?: Interconnection[]): Promise<any[]> {
		setloadingState(true);
		const eventDuration: number = durationToSeconds(durationPicker.fst);
		let rawTravelDuration = parseFloat(durationPicker.snd.nb);
		if (isNaN(rawTravelDuration)) {
			rawTravelDuration = 0;
		}
		const travelDuration: number = durationToSeconds(durationPicker.snd);
		const visits: OptimizationVisit[] = (companies ?? props.companies).map((c, i) => ({
			id: c.id,
			duration: eventDuration,
			lon: c.lon,
			lat: c.lat,
			travel_duration: interconnections?.[i]?.time ?? travelDuration
		}));

		const body: VisitsOptimizationBody = {
			visits,
			first_visit: momentToNaiveDateTime(moment(firstEventDate).set({ hour: firstEventHour.getHours(), minute: firstEventHour.getMinutes(), second: 0, millisecond: 0 })),
			working_hours: {
				start: dateToNaiveDateTime(hourStartDay),
				end: dateToNaiveDateTime(hourEndDay)
			},
			pause: pauseHour ? {
				time: dateToNaiveDateTime(pauseHour),
				duration: durationToSeconds(durationPicker.trd)
			} : null,
			working_days: daysSelected.map(ds => WEEK_DAY_LIST[ds]),
			max_event_day: parseInt(maxEvent),
		};
		try {
			const res = await optimizeVisits(body);
			return res.reduce((acc: any[], r: RetVisit) => {
				const company = (companies ?? props.companies).find(c => r.id === (c.id ?? c.company_id));
				if (!company) return acc;
				acc.push({
					eventTypeId: props.type?.id,
					eventStatusId: 1,
					title: `${title ? title : name} -- ${company.name ?? ''}`,
					companyName: company.name,
					clientCompanyId: (company.id ?? company.company_id)?.toString(),
					description,
					incharge: `${owner?.id}`,
					startDate: new Date(r.start),
					endDate: new Date(r.end),
				});
				return acc;
			}, []);
		} catch (e) {
			console.error(e);
			throw new Error(translateString('map.modal_right.error_creating_events'));
		}
	}

	function isInWorkDay(lowerBound: Date) {
		const trueStartDay: Date = new Date(2000, 1, 1, hourStartDay.getHours(), hourStartDay.getMinutes(), 0, 0);
		const trueEndDay: Date = new Date(2000, 1, 1, hourEndDay.getHours(), hourEndDay.getMinutes(), 0, 0);
		const toCompareLower: Date = new Date(2000, 1, 1, lowerBound.getHours(), lowerBound.getMinutes(), 0, 0);
		if (trueStartDay.getTime() < trueEndDay.getTime()) {
			return trueStartDay.getTime() <= toCompareLower.getTime() && trueEndDay.getTime() > toCompareLower.getTime();
		}
		return !(trueStartDay.getTime() > toCompareLower.getTime() && trueEndDay.getTime() <= toCompareLower.getTime());
	}

	const [loadingState, setloadingState] = React.useState<boolean>(false);

	return (
		<>
			{!props.noTitle &&
				<div style={{
					fontFamily: 'Inter',
					fontStyle: 'normal',
					fontWeight: 'bold',
					fontSize: '14px',
					color: SidelyBlack,
					margin: '22px 0px 40px 25px'
				}}>
					<Translate id='map.modal_right.plan_your' />{name}{name.toLowerCase().slice(-1) === 's' ? '' : 's'}
				</div>
			}

			<div style={{
				overflow: 'auto',
				padding: '0 25px',
				height: !props.noTitle ? 'calc(100vh - 291px)' : 'calc(100vh - 148px - 5%)'
			}}>
				<div style={{ height: '73px', paddingTop: '5px' }}>
					<DropdownOwners
						disabled={false}
						readOnly
						dropdownStyle={creationPopupDropdownStyle}
						users={owners.map(o => { return { label: o.name, value: o, image: o.photoUrl }; })}
						selected={(owner != null) ? { label: owner.name, value: owner, image: owner.photoUrl } : undefined}
						border
						label={translate('company.creation.contact.owner').toString()}
						onChange={value => {
							setOwner(value.value ?? undefined);
						}}
					/>
				</div>
				<Titles><Translate id='title' /></Titles>
				<div style={{ height: '55px' }}>
					<div style={{
						background: SurfaceColor,
						border: `1px solid ${getError('title') ? RedSidely : BorderColor}`,
						boxSizing: 'border-box',
						borderRadius: '4px',
						height: `${INPUT_HEIGHT}px`,
						width: '100%'
					}}>
						<Input
							type='text'
							value={title}
							onChange={e => setTitle(e)}
							placeholder={translateToString('title')}
							name={''}
							inputStyle={{
								border: 'none',
								width: '100%',
								height: '100%'
							}}
						/>
					</div>
					<ErrorMessage>{getError('title')}</ErrorMessage>
				</div>

				<Titles><Translate id='description' /></Titles>
				<Input
					type='text'
					value={description}
					onChange={e => setDescription(e)}
					placeholder={translateToString('description')}
					name={''}
					textArea
					inputStyle={{ containerWidth: '100%', fontSize: '12px', height: '10rem', margin: '0 0 25px 0', padding: '14px' }}
				/>

				<Titles><Translate id='map.modal_right.max_events_per_day' /></Titles>
				<div style={{ height: 80 }}>
					<Input
						type='text'
						value={maxEvent}
						onChange={e => {
							const digits = e.replace(/[^0-9]/g, '');
							setMaxEvent(digits);
							localStorage.setItem('planEventMaxEvent', digits);
						}}
						placeholder={'Max'}
						name={''}
						inputStyle={{
							containerWidth: '100%',
							width: '100%',
							fontSize: '12px',
							border: getError('max_events_per_day') ? `1px solid ${RedSidely}` : undefined,
							padding: '14px'
						}}
					/>
					<ErrorMessage>{getError('max_events_per_day')}</ErrorMessage>
				</div>


				<Restricted to={{ objectAction: 'ReadForm' }}>
					<Dropdown<FormTemplate>
						disabled={false}
						placeholder={translateToString('select_template')}
						dropdownStyle={creationPopupDropdownStyle}
						datalist={formTemplates.map(o => ({ label: o.name, value: o }))}
						name='dropdown_forms'
						readOnly
						cancellable
						label={translate('form').toString()}
						onChange={value => {
							setForm(value?.value);
							localStorage.setItem('planEventForm', JSON.stringify(value?.value));
						}}
						selectedValue={form ? { label: form.name, value: form } : undefined}
					/>
					<Spacer />
				</Restricted>
				<Titles><Mandatory>* </Mandatory><Translate id='map.modal_right.first_event' /></Titles>
				<div style={{ height: '60px' }}>
					<DayPicker
						noAbsolute
						noDayButton
						future
						date={firstEventDate ? moment(firstEventDate) : undefined}
						onDateChange={date => {
							setFirstEventDate(date.toDate());
							localStorage.setItem('planEventFirstEventDate', JSON.stringify(date.toDate()));
						}}
					/>
				</div>
				<div style={{ height: '75px' }}>
					<SimpleTimePicker
						currDate={firstEventHour}
						onChange={(e) => {
							if (e)
								setFirstEventHour(e);
							localStorage.setItem('planEventFirstEventHour', JSON.stringify(e));
						}}
						error={getError('first_event_hour')}
					/>
					<ErrorMessage>{getError('first_event_hour')}</ErrorMessage>
				</div>
				<Titles><Mandatory>* </Mandatory><Translate id='map.modal_right.duration_each_event' /></Titles>
				<div style={{ height: '75px' }}>
					<SimpleDurationPicker duration={durationPicker.fst} setTimeOperator={(e) => {
						const newDurationPicker = { ...durationPicker, fst: { ...durationPicker.fst, ...e } };
						setDurationPicker(newDurationPicker);
						localStorage.setItem('planEventDurationPicker', JSON.stringify(newDurationPicker));
					}} placeholder={'duration'} error={getError('duration_each_event')} />
					<ErrorMessage>{getError('duration_each_event')}</ErrorMessage>
				</div>
				<Titles><Translate id='map.modal_right.time_between_each_event' /></Titles>
				<div style={{ height: '75px' }}>
					<SimpleDurationPicker duration={durationPicker.snd} setTimeOperator={(e) => {
						const newDurationPicker = { ...durationPicker, snd: { ...durationPicker.snd, ...e } };
						setDurationPicker(newDurationPicker);
						localStorage.setItem('planEventDurationPicker', JSON.stringify(newDurationPicker));
					}} placeholder={'time'} error={getError('time_between_each_event')} />
					<ErrorMessage>{getError('time_between_each_event')}</ErrorMessage>
				</div>

				<Titles><Mandatory>* </Mandatory><Translate id='map.modal_right.hour_start_day' /></Titles>
				<div style={{ height: '75px' }}>
					<SimpleTimePicker
						onChange={(e) => {
							if (e) setHourStartDay(e);
							localStorage.setItem('planEventHourStartDay', e.getHours().toString());
						}}
						currDate={hourStartDay}
						error={getError('hour_start_day')}
					/>
					<ErrorMessage>{getError('hour_start_day')}</ErrorMessage>
				</div>
				<Titles><Mandatory>* </Mandatory><Translate id='map.modal_right.hour_end_day' /></Titles>
				<div style={{ height: '75px' }}>
					<SimpleTimePicker
						onChange={(e) => {
							if (e) setHourEndDay(e);
							localStorage.setItem('planEventHourEndDay', e.getHours().toString());
						}}
						currDate={hourEndDay}
						error={getError('hour_end_day')}
					/>
					<ErrorMessage>{getError('hour_end_day')}</ErrorMessage>
				</div>

				<Titles><Translate id='map.modal_right.pause_time' /></Titles>
				<div style={{ height: '75px' }}>
					<SimpleTimePicker
						onChange={(e) => {
							setPauseHour(e);
							localStorage.setItem('planEventPauseHour', JSON.stringify(e));
						}}
						currDate={pauseHour}
						placeholder={translate('hour').toString()}
					/>
					<ErrorMessage>{getError('pause_time')}</ErrorMessage>
				</div>
				<Titles><Translate id='map.modal_right.duration_pause' /></Titles>
				<div style={{ height: '75px' }}>
					<SimpleDurationPicker duration={durationPicker.trd} setTimeOperator={(e) => {
						const newDurationPicker = { ...durationPicker, trd: { ...durationPicker.trd, ...e } };
						setDurationPicker(newDurationPicker);
						localStorage.setItem('planEventDurationPicker', JSON.stringify(newDurationPicker));
					}} placeholder={'duration'} error={getError('duration_pause')} />
					<ErrorMessage>{getError('duration_pause')}</ErrorMessage>
				</div>
				<Titles><Mandatory>* </Mandatory><Translate id='map.modal_right.days_of_activity' /></Titles>
				<ErrorMessage>{getError('days_of_activity')}</ErrorMessage>
				<CheckBoxWorkdayList selectedDays={daysSelected} setSelectedDays={setDaysSelected} />

			</div>
			<FlexDiv justify='space-evenly' height='60px' margin='20px 0'>
				<Button
					buttonStyle={ButtonStyle.White}
					onClick={() => {
						setGlobalError(false);
						props.onBack();
					}}
				>
					<Translate id='back' />
				</Button>
				{loadingState ? <PageLoader></PageLoader> : <Button
					buttonStyle={buttonError ? ButtonStyle.Error : undefined}

					onClick={() => {
						if (asAnError()) {
							setGlobalError(true);
							setButtonError(true);
							setTimeout(() => {
								setButtonError(false);
							}, 750);
						} else {
							setGlobalError(false);
							setButtonError(false);
							const trueStartDay = new Date(2000, 1, 1, hourStartDay.getHours(), hourStartDay.getMinutes(), 0, 0);
							const trueEndDay = new Date(2000, 1, 1, hourEndDay.getHours(), hourEndDay.getMinutes(), 0, 0);
							if (trueStartDay.getTime() > trueEndDay.getTime()) {
								trueEndDay.setDate(2);
							}
							const truePause = (pauseHour != null) ? new Date(2000, 1, 1, pauseHour.getHours(), pauseHour.getMinutes(), 0, 0) : null;
							if ((truePause != null) && trueStartDay.getTime() > truePause.getTime()) {
								truePause.setDate(2);
							}
							const pauseDuration: number = durationToMiliSeconds(durationPicker.trd);
							const eventDuration = durationToMiliSeconds(durationPicker.fst);
							const dayDuration = trueEndDay.getTime() - trueStartDay.getTime();
							const morningDuration = (truePause == null) ? dayDuration : truePause.getTime() - trueStartDay.getTime();
							const afternoonDuration = (truePause == null) ? dayDuration : trueEndDay.getTime() - truePause.getTime() - pauseDuration;
							if (props.companies.length > MAX_COMPANIES && !props.noLimit) {
								toast.error(translate('map.modal_right.can_t_create_more_than_100_events_at_a_time') as string);
							} else if ((pauseHour != null) && !isInWorkDay(pauseHour)) {
								toast.error(translate('map.modal_right.pause_out_of_work_day') as string);
							} else if (eventDuration > dayDuration || eventDuration > morningDuration || eventDuration > afternoonDuration) {
								toast.error(translate('map.modal_right.event_duration_to_big') as string);
							} else {
								createEventsList()
									.then(res => {
										setEvents(res);
										setModalOpen(!modalOpen);
										setloadingState(false);
									})
									.catch(e => {
										toast.error(e.message || translate('map.modal_right.error_creating_events') as string);
										setloadingState(false);
									});
							}
						}
					}}
				>
					<Translate id='next' />
				</Button>
				}
			</FlexDiv>
			<ModalCalendar
				onEventCreate={props.onCreate}
				v2={props.v2}
				isVisit={eventTypes.some(e => e.id === props.type?.id && e.map)}
				isOpen={modalOpen}
				toggleModal={() => setModalOpen(!modalOpen)}
				events={events}
				name={name}
				form_id={form?.id}
				onClose={props.onBack}
				id={owner?.id ?? 0}
				companies={props.companies}
				onOptimize={(ids: string[], interconnections: Interconnection[]) => {
					const newCompanies = ids.map(id => props.companies.find(c => c.id.toString() == id));
					createEventsList(newCompanies, interconnections).then(setEvents);
				}}
				maxVisitsPerDay={maxEvent ? parseInt(maxEvent) : undefined}
				hourStartDay={hourStartDay}
				hourEndDay={hourEndDay}
				pauseHour={pauseHour ?? undefined}
				visitDuration={durationPicker.fst}
				pauseDuration={durationToMiliSeconds(durationPicker.trd)}
				daysSelected={daysSelected}
				firstEventDate={moment(firstEventDate).set('minute', firstEventHour.getMinutes()).set('hour', firstEventHour.getHours()).set('second', 0).toDate()}
				firstEventHour={firstEventHour.getHours()}
				hotelNights={hotelNights}
				onTSPOptimize={(tspResponse, points, hotelNights, workingDays) => {
					const firstDate = moment(workingDays?.startDate ?? firstEventDate);
					const workDays = workingDays?.workdays ?? daysSelected;
					const finalEvents: unknown[] = [];
					for (const route of tspResponse.routes) {
						for (const stop of route.stops) {
							if (stop.arrival === stop.departure) continue;
							const companyId = points[stop.node]?.id;
							if (!companyId) continue;
							const company = props.companies.find(c => companyId === (c.id ?? c.company_id));
							if (!company) continue;

							const date = firstDate.clone().add(stop.day, 'days');
							let day = date.day() - 1;
							if (day === -1) day = 6;
							while (!workDays.includes(day)) {
								date.add(1, 'day');
								firstDate.add(1, 'day');
								day = date.day() - 1;
								if (day === -1) day = 6;
							}
							const arrival = moment(stop.arrival, 'HH:mm', true);
							const departure = moment(stop.departure, 'HH:mm', true);
							const departureDate = date.set({ hour: departure.hour(), minute: departure.minute() });
							const arrivalDate = date.clone().set({ hour: arrival.hour(), minute: arrival.minute() });
							finalEvents.push({
								eventTypeId: props.type?.id,
								eventStatusId: 1,
								title: `${title ? title : name} -- ${company.name ?? ''}`,
								companyName: company.name,
								clientCompanyId: companyId.toString(),
								description,
								incharge: `${owner?.id}`,
								startDate: arrivalDate.toDate(),
								endDate: departureDate.toDate(),
							});
						}
					}
					setHotelNights(hotelNights);
					setEvents(finalEvents);
				}}
			/>
		</>
	);
}
